import React, { useState, useEffect, useRef } from 'react';
import { useContracts } from '../../hooks/useContract';
import { getActivityName, formatUsdt, timeAgo } from '../../utils/format';
import Card from '../common/Card';
import MiniLoader from '../common/MiniLoader';

const ACTIVITY_ICONS = {
  1: '📝', 2: '📦', 3: '💰', 4: '📈', 5: '👑', 6: '💎',
  7: '⏸', 8: '💧', 9: '⬆', 10: '⬆', 11: '📤', 12: '🏦',
  13: '⛔', 14: '⚠', 15: '🚫', 16: '✅', 17: '✏', 18: '🔑'
};

const ActivityFeed = ({ userId, limit = 20 }) => {
  const { storage } = useContracts();
  const [activities, setActivities] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const runIdRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!userId) return;
      const myRun = ++runIdRef.current;
      setLoading(true);
      try {
        const [acts, , totalCount] = await storage.getUserActivities(userId, 0, limit);
        if (cancelled || myRun !== runIdRef.current) return;
        setActivities(acts.map((a) => ({
          activityType: a.activityType,
          timestamp: a.timestamp,
          relatedId: a.relatedId.toString(),
          amount: a.amount,
          netAmount: a.netAmount
        })));
        setTotal(totalCount.toNumber ? totalCount.toNumber() : Number(totalCount));
      } catch (_) { /* ignore */ }
      finally {
        if (myRun === runIdRef.current) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [userId, limit]);

  return (
    <Card title="Recent Activity" subtitle={`Total: ${total} activities`}>
      {loading && (
        <div className="text-center" style={{ padding: 40 }}><MiniLoader /></div>
      )}

      {!loading && activities.length === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon">📋</div>
          <div>No activity yet</div>
        </div>
      )}

      {!loading && activities.length > 0 && (
        <div className="flex flex-col gap-sm">
          {activities.map((a, idx) => (
            <div key={idx} className="flex items-center gap-md" style={{
              padding: 12,
              background: 'rgba(10, 14, 39, 0.4)',
              borderRadius: 'var(--radius-sm)',
              borderLeft: '3px solid var(--aurora-cyan)'
            }}>
              <div style={{ fontSize: 20 }}>{ACTIVITY_ICONS[a.activityType] || '•'}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>
                  {getActivityName(a.activityType)}
                </div>
                <div className="text-muted" style={{ fontSize: 12 }}>
                  {timeAgo(a.timestamp)}
                  {a.relatedId && a.relatedId !== '0' && (
                    <> · Related: {a.relatedId}</>
                  )}
                </div>
              </div>
              {!a.amount.isZero() && (
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }} className="text-cyan">
                    {formatUsdt(a.amount)}
                  </div>
                  {!a.netAmount.isZero() && (
                    <div className="text-muted" style={{ fontSize: 11 }}>
                      Net: {formatUsdt(a.netAmount)}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default ActivityFeed;
